import {
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {Locale} from '@dms/api/base';
import {useAddTemplateMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError, useTenant} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {getDocumentTemplateEditorPath} from '../../DocumentTemplateEditor/utils/getDocumentTemplateEditorPath';
import {MINIMAL_HTML_SCAFFOLD} from '../constants/MINIMAL_HTML_SCAFFOLD';

interface CreateBlankTemplateFormProps {
  authorizationProfileId: string | null;
  documentCode: string;
}

const baseSchema = z.object({
  title: z.string().nonempty(),
  locale: z.custom<Locale>((value) => typeof value === 'string').optional(),
});
const multiLocaleSchema = baseSchema.extend({
  locale: z.custom<Locale>((value) => typeof value === 'string'),
});
type FormValues = z.infer<typeof baseSchema>;

export function CreateBlankTemplateForm(props: CreateBlankTemplateFormProps) {
  const [addTemplate, {isLoading}] = useAddTemplateMutation();
  const {isMultiLocale, tenant, supportedLocales} = useTenant();
  const navigate = useNavigate();

  const handleSubmit: FormSubmitHandler<FormValues> = (values) => {
    const locale = match(isMultiLocale)
      .with(true, () => values.locale)
      .with(false, () => null)
      .exhaustive();

    return addTemplate({
      addTemplateRequestBody: {
        authorizationProfileId: props.authorizationProfileId,
        documentCode: props.documentCode,
        fileId: null,
        template: MINIMAL_HTML_SCAFFOLD,
        title: values.title,
        note: null,
        locale,
      },
    })
      .unwrap()
      .then(({id}) => {
        closeCurrentDialog();
        navigate(getDocumentTemplateEditorPath(id));
      })
      .catch(handleApiError);
  };

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      defaultValues={{title: '', locale: tenant?.defaultLocale}}
      experimentalZodSchema={match(isMultiLocale)
        .with(true, () => multiLocaleSchema)
        .with(false, () => baseSchema)
        .exhaustive()}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            name="title"
            label={i18n.t('entity.document.labels.templateTitle')}
            type="text"
            isRequired
          />
          <Show when={isMultiLocale}>
            <FormField
              control={control}
              name="locale"
              type="choice"
              label={i18n.t('general.labels.locales')}
              options={supportedLocales}
              isNotClearable
              isRequired
            />
          </Show>
          <ButtonGroup align="right">
            <FormButton
              variant="secondary"
              title={i18n.t('general.actions.discard')}
              control={control}
              onClick={closeCurrentDialog}
            />
            <FormButton
              type="submit"
              variant="primary"
              control={control}
              isDisabled={isLoading}
              title={i18n.t('entity.documentTemplateEditor.actions.createBlankTemplate')}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
